import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, CheckCircle, WarningCircle, WhatsappLogo, PlayCircle } from "@phosphor-icons/react";
import PhoneInput from "../ui/PhoneInput";
import LoaderLoop from "../shared/loader/LoaderLoop";
import { useCreateLeadFromLandingPageMutation } from "../../store/services/nivApi";
import { trackPixelEvent } from "../../hooks/useFacebookPixel";
import {
  FORM,
  START_LEAD_EXTRA_FIELDS,
  getMessagePlaceholder,
  getWhatsAppHref,
} from "../../data/startPageContent";
import "./StartLeadForm.css";

const SOURCE = "get_started_page";

/** Normalise whatever PhoneInput emits into +972XXXXXXXXX, or null when invalid. */
const toInternational = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 10 && digits.startsWith("05")) return `+972${digits.slice(1)}`;
  if (digits.length === 12 && digits.startsWith("9725")) return `+${digits}`;
  return null;
};

const storageKey = (phone) => `${FORM.storagePrefix}${phone.replace(/\D/g, "")}`;

const readRecentSubmit = (phone) => {
  try {
    const raw = window.localStorage.getItem(storageKey(phone));
    if (!raw) return false;
    const ts = Number(raw);
    return Number.isFinite(ts) && Date.now() - ts < FORM.dedupeHours * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

const writeRecentSubmit = (phone) => {
  try {
    window.localStorage.setItem(storageKey(phone), String(Date.now()));
  } catch {
    /* storage unavailable (private mode) — ignore */
  }
};

/**
 * 3-field lead form for /start (plan §5).
 *
 * @param {"hero"|"bottom"} variant   — id/anchor + layout hook
 * @param {string|null}     profession — selected profession key (stored on the lead)
 * @param {object|null}     utm        — captured UTM map (stored on the lead)
 * @param {function}        onSuccess  — optional callback after a successful submit
 */
export default function StartLeadForm({ variant = "hero", profession = null, utm = null, onSuccess }) {
  const [createLead, { isLoading }] = useCreateLeadFromLandingPageMutation();
  const [values, setValues] = useState({ fullName: "", phone: "", message: "", website: "" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const mountedAt = useRef(Date.now());
  const lockRef = useRef(false);
  const textareaRef = useRef(null);

  const isHero = variant === "hero";
  const formId = isHero ? "start-form" : "start-form-bottom";
  const fieldId = (name) => `${formId}-${name}`;

  // Rotate the textarea placeholder while it is empty — only when motion is OK.
  useEffect(() => {
    if (values.message) return undefined;
    if (typeof window === "undefined" || navigator.webdriver) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = window.setInterval(() => setPlaceholderIdx((i) => i + 1), 4500);
    return () => window.clearInterval(t);
  }, [values.message]);

  // Move focus to the success heading so screen readers announce it.
  const successTitleRef = useRef(null);
  useEffect(() => {
    if (submitted) successTitleRef.current?.focus();
  }, [submitted]);

  // Autogrow textarea: 2 → 4 rows.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = 22;
    const max = lineHeight * 4 + 18;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  }, [values.message]);

  const setField = (name) => (value) => {
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const validate = useCallback(() => {
    const next = {};
    if (values.fullName.trim().length < FORM.limits.fullNameMin || values.fullName.trim().length > FORM.limits.fullNameMax) {
      next.fullName = FORM.errors.fullName;
    }
    if (!toInternational(values.phone)) next.phone = FORM.errors.phone;
    if (values.message.length > FORM.limits.messageMax) next.message = FORM.errors.message;
    return next;
  }, [values]);

  const onBlur = (name) => () => {
    const next = validate();
    setErrors((e) => ({ ...e, [name]: next[name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lockRef.current || isLoading) return;
    setSubmitError(false);

    // Honeypot: bots fill the hidden field → pretend success, write nothing.
    if (values.website) {
      setSubmitted(true);
      return;
    }

    const next = validate();
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    // Dwell guard: humans need > 2 s to fill three fields.
    if (Date.now() - mountedAt.current < FORM.dwellMs) {
      setErrors({ tooFast: FORM.errors.tooFast });
      return;
    }

    const phone = toInternational(values.phone);

    // Same phone already submitted in the last 24 h → success state, no write.
    if (readRecentSubmit(phone)) {
      setSubmitted(true);
      onSuccess?.({ deduped: true });
      return;
    }

    lockRef.current = true;
    try {
      const payload = {
        fullName: values.fullName.trim(),
        phone,
        message: values.message.trim() || null,
        source: SOURCE,
      };
      if (START_LEAD_EXTRA_FIELDS) {
        if (profession) payload.profession = profession;
        if (utm && Object.keys(utm).length > 0) payload.utm = utm;
      }

      const result = await createLead(payload);
      if (result?.error) throw new Error(result.error.message || "lead_create_failed");

      writeRecentSubmit(phone);
      trackPixelEvent("Lead", { content_name: SOURCE, profession: profession || undefined });
      setSubmitted(true);
      onSuccess?.({ deduped: false });
    } catch (error) {
      console.error("[CK-1056] StartLeadForm submit failed", {
        errorCode: error?.code || "CK-1056",
        errorMessage: error?.message,
        source: SOURCE,
        profession: profession || null,
        variant,
        timestamp: new Date().toISOString(),
      });
      setSubmitError(true);
    } finally {
      lockRef.current = false;
    }
  };

  if (submitted) {
    return (
      <div id={formId} className={`st-form st-form--${variant} st-form--success`}>
        <div className="st-form-success-icon" aria-hidden="true">
          <CheckCircle size={40} weight="fill" />
        </div>
        <h2 className="st-form-title" ref={successTitleRef} tabIndex={-1}>{FORM.success.title}</h2>
        <p className="st-form-sub">{FORM.success.text}</p>
        <div className="st-form-success-actions">
          <span className="st-form-success-prompt">{FORM.success.waPrompt}</span>
          <a
            className="st-btn st-btn--ghost st-form-wa"
            href={getWhatsAppHref()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackPixelEvent("Contact", { content_name: SOURCE })}
          >
            <WhatsappLogo size={20} weight="fill" />
            {FORM.success.waButton}
          </a>
        </div>
        <a className="st-form-tour" href={FORM.success.tourHref}>
          <PlayCircle size={18} />
          {FORM.success.tourPrompt}
        </a>
      </div>
    );
  }

  return (
    <form
      id={formId}
      className={`st-form st-form--${variant}`}
      onSubmit={handleSubmit}
      noValidate
      aria-labelledby={fieldId("title")}
    >
      <div className="st-form-head">
        <span className="st-form-live" aria-hidden="true">
          <i className="st-form-live-dot" />
          {FORM.availabilityDot}
        </span>
        <h2 id={fieldId("title")} className="st-form-title">{FORM.title}</h2>
        <p className="st-form-sub">{FORM.sub}</p>
      </div>

      {/* Honeypot — invisible to humans, tempting to bots */}
      <div className="st-form-hp" aria-hidden="true">
        <label htmlFor={fieldId("website")}>אתר</label>
        <input
          id={fieldId("website")}
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={(e) => setField("website")(e.target.value)}
        />
      </div>

      <div className={`st-field${errors.fullName ? " st-field--error" : ""}`}>
        <label htmlFor={fieldId("fullName")} className="st-label">{FORM.labels.fullName}</label>
        <input
          id={fieldId("fullName")}
          name="fullName"
          type="text"
          className="st-input"
          autoComplete="name"
          placeholder={FORM.placeholders.fullName}
          value={values.fullName}
          onChange={(e) => setField("fullName")(e.target.value)}
          onBlur={onBlur("fullName")}
          maxLength={FORM.limits.fullNameMax}
          disabled={isLoading}
          aria-invalid={Boolean(errors.fullName)}
          aria-describedby={errors.fullName ? fieldId("fullName-err") : undefined}
        />
        {errors.fullName && (
          <span id={fieldId("fullName-err")} className="st-field-error" role="alert">
            <WarningCircle size={14} weight="fill" />
            {errors.fullName}
          </span>
        )}
      </div>

      <div className={`st-field${errors.phone ? " st-field--error" : ""}`}>
        <span id={fieldId("phone-label")} className="st-label">{FORM.labels.phone}</span>
        <div
          role="group"
          aria-labelledby={fieldId("phone-label")}
          aria-describedby={errors.phone ? fieldId("phone-err") : undefined}
          onBlur={onBlur("phone")}
        >
          <PhoneInput
            value={values.phone}
            onChange={setField("phone")}
            error={errors.phone}
            disabled={isLoading}
          />
        </div>
        {errors.phone && (
          <span id={fieldId("phone-err")} className="st-sr-only" role="alert">
            {errors.phone}
          </span>
        )}
      </div>

      <div className={`st-field${errors.message ? " st-field--error" : ""}`}>
        <label htmlFor={fieldId("message")} className="st-label">
          {FORM.labels.message}
          <span className="st-label-hint">{FORM.optionalHint}</span>
        </label>
        <textarea
          id={fieldId("message")}
          name="message"
          ref={textareaRef}
          className="st-input st-textarea"
          rows={2}
          placeholder={getMessagePlaceholder(placeholderIdx, profession)}
          value={values.message}
          onChange={(e) => setField("message")(e.target.value)}
          onBlur={onBlur("message")}
          maxLength={FORM.limits.messageMax + 50}
          disabled={isLoading}
          aria-invalid={Boolean(errors.message)}
          aria-describedby={errors.message ? fieldId("message-err") : undefined}
        />
        {errors.message && (
          <span id={fieldId("message-err")} className="st-field-error" role="alert">
            <WarningCircle size={14} weight="fill" />
            {errors.message}
          </span>
        )}
      </div>

      {errors.tooFast && (
        <p className="st-field-error st-form-generic-error" role="alert">
          <WarningCircle size={14} weight="fill" />
          {errors.tooFast}
        </p>
      )}

      {submitError && (
        <p className="st-field-error st-form-generic-error" role="alert">
          <WarningCircle size={14} weight="fill" />
          {FORM.errors.submit}{" "}
          <a href={getWhatsAppHref()} target="_blank" rel="noopener noreferrer">
            {FORM.errors.submitWaLink}
          </a>
        </p>
      )}

      <button type="submit" className="st-btn st-btn--primary st-form-submit" disabled={isLoading}>
        {isLoading ? (
          <>
            <LoaderLoop size="xs" />
            {FORM.buttonLoading}
          </>
        ) : (
          <>
            {FORM.button}
            <ArrowLeft size={18} weight="bold" />
          </>
        )}
      </button>
      <p className="st-fineprint st-form-fineprint">{FORM.fineprint}</p>
    </form>
  );
}
